import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusPill from "@/components/StatusPill";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Plus, ChevronRight, FileText, Trash2, ArrowRight, AlertTriangle, Search } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Projects = () => {
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [query, setQuery] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);
  const navigate = useNavigate();

  const refresh = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { refresh(); }, []);

  const drafts = projects.filter((p) => p.status === "draft");
  // V-P1 · Projects landing — recent-first ordering + status pill.
  // Non-draft projects sorted by updated_at || created_at descending.
  const active = useMemo(() => {
    const list = projects.filter((p) => p.status !== "draft");
    return [...list].sort((a, b) => {
      const at = new Date(a.updated_at || a.created_at || 0).getTime();
      const bt = new Date(b.updated_at || b.created_at || 0).getTime();
      return bt - at;
    });
  }, [projects]);

  const q = query.trim().toLowerCase();
  const filteredActive = q
    ? active.filter((p) =>
        (p.customer_name || "").toLowerCase().includes(q) ||
        (p.job_type || "").toLowerCase().includes(q) ||
        (p.job_address || "").toLowerCase().includes(q)
      )
    : active;

  const resumeDraft = (p) => {
    // Drafts have a project record but no estimate yet — Project Detail is where
    // the contractor can finish editing fields, add photos, and create the estimate.
    navigate(`/app/projects/${p.id}?resume=1`);
  };

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${draftToDelete.id}`);
      toast.success(t("projects_list.toast_draft_deleted"));
      setDraftToDelete(null);
      await refresh();
    } catch {
      toast.error(t("projects_list.toast_draft_delete_failed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout
      title={t("pages.projects.title")}
      subtitle={t("pages.projects.subtitle")}
      actions={
        <Link
          to="/app/estimates/new"
          data-testid="projects-new-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-blue"
        >
          <Plus size={14} /> {t("pages.estimates.new_btn")}
        </Link>
      }
    >
      {projects.length === 0 ? (
        <div className="jc-card p-12 text-center">
          <div className="font-display text-xl">{t("projects_list.empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("projects_list.empty_body_short")}</p>
          <Link to="/app/estimates/new" className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-md jc-btn-blue text-sm font-semibold">
            <Plus size={14} /> {t("projects_list.start_estimate")}
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* V-P1 · S4 search bar (prominent) */}
          <div className="jc-card p-3" data-testid="projects-search-card">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#EFF6FF] border border-[var(--jc-border-strong)]">
              <Search size={16} className="text-[var(--jc-blue-700)] shrink-0" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("projects_list.search_placeholder")}
                data-testid="projects-search-input"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
          </div>

          {/* V-P1 · Drafts collapsed into a small Resume drafts (N) affordance */}
          {drafts.length > 0 && (
            <section data-testid="drafts-section" className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowDrafts((v) => !v)}
                data-testid="drafts-toggle-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-[var(--jc-yellow)]/50 bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A]"
              >
                <FileText size={12} /> {t("projects_list.resume") || "Resume"} drafts ({drafts.length}) {showDrafts ? "▲" : "▼"}
              </button>
              <span className="text-[11px] text-[var(--jc-text-soft)]">
                {t("projects_list.drafts_help")}
              </span>
              {showDrafts && (
                <div className="jc-card overflow-hidden border-l-4 border-l-[var(--jc-yellow)] w-full mt-1" data-testid="drafts-table">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold">{t("projects_list.columns.customer")}</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("projects_list.columns.job")}</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("projects_list.columns.started")}</th>
                      <th className="text-right px-5 py-3 font-semibold">{t("projects_list.columns.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--jc-border)]">
                    {drafts.map((p) => (
                      <tr key={p.id} className="hover:bg-[var(--jc-bg)]" data-testid={`draft-row-${p.id}`}>
                        <td className="px-5 py-3 font-semibold">{p.customer_name || t("projects_list.untitled_customer")}</td>
                        <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{p.job_type || "—"}</td>
                        <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{formatDate(p.created_at)}</td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => resumeDraft(p)}
                              data-testid={`resume-draft-${p.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
                            >
                              {t("projects_list.resume")} <ArrowRight size={12} />
                            </button>
                            <button
                              onClick={() => setDraftToDelete(p)}
                              data-testid={`delete-draft-${p.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] text-[var(--jc-text-soft)] hover:text-[var(--jc-red)] hover:border-[var(--jc-red)]"
                              title={t("projects_list.delete")}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </section>
          )}

          <section>
            <div className="overline mb-3">{t("projects_list.active_heading")} ({filteredActive.length})</div>
            {filteredActive.length === 0 ? (
              <div className="jc-card p-10 text-center">
                <div className="font-display text-lg">{t("projects_list.active_empty_title")}</div>
                <p className="text-sm text-[var(--jc-text-soft)] mt-2">
                  {drafts.length > 0
                    ? t("projects_list.active_empty_body_drafts_present")
                    : t("projects_list.active_empty_body_no_drafts")}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-active-grid">
                {filteredActive.map((p) => (
                  <Link
                    key={p.id}
                    to={`/app/projects/${p.id}`}
                    data-testid={`project-row-${p.id}`}
                    className="jc-card p-4 jc-card-hover group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-[var(--jc-text-soft)] truncate">
                          {p.customer_name || "—"}
                        </div>
                        <div className="font-display text-lg mt-0.5 truncate">
                          {p.job_type || p.customer_name || t("projects_list.untitled_customer")}
                        </div>
                      </div>
                      <StatusPill status={p.status} kind="project" t={t} />
                    </div>
                    {p.job_address && (
                      <div className="text-xs text-[var(--jc-text-soft)] truncate mt-2">
                        {p.job_address}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--jc-border)]">
                      <div className="text-[11px] text-[var(--jc-text-soft)]">
                        {formatDate(p.updated_at || p.created_at)}
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--jc-blue)] group-hover:gap-2 transition-all">
                        {t("projects_list.open")} <ChevronRight size={12} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <p className="text-xs text-[var(--jc-text-soft)] text-center italic">
            All projects reachable · every existing route preserved · no new lifecycle state introduced.
          </p>
        </div>
      )}

      <AlertDialog open={!!draftToDelete} onOpenChange={(open) => { if (!open) setDraftToDelete(null); }}>
        <AlertDialogContent data-testid="delete-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--jc-red)]/10 text-[var(--jc-red)]">
                <AlertTriangle size={18} />
              </span>
              {t("projects_list.delete_dialog_title", draftToDelete?.customer_name || t("projects_list.untitled_customer"))}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("projects_list.delete_dialog_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-draft-cancel-btn" disabled={deleting}>
              {t("projects_list.keep_draft")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-draft-confirm-btn"
              onClick={(e) => { e.preventDefault(); confirmDeleteDraft(); }}
              disabled={deleting}
              className="bg-[var(--jc-red)] text-white hover:bg-[var(--jc-red)]/90 focus:ring-[var(--jc-red)]"
            >
              {deleting ? t("projects_list.deleting") : t("projects_list.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Projects;
